/*
 * @Description: 移动端日期选择器组件，基于Picker组件
 */
'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import Picker from '../../Picker';
import Popup from '../../Popup';

export interface DateRange {
  minDate?: Date;
  maxDate?: Date;
}

export interface MobileDatePickerProps {
  /**
   * 选择器的值
   */
  value?: Date;
  /**
   * 默认值
   */
  defaultValue?: Date;
  /**
   * 日期选择变化的回调函数
   */
  onChange?: (date: Date) => void;
  /**
   * 自定义 CSS 类名
   */
  className?: string;
  /**
   * 日期范围限制
   */
  dateRange?: DateRange;
  /**
   * 选择器高度
   */
  optionHeight?: number | string;
  /**
   * 可见选项数量
   */
  visibleOptionNum?: number;

  /**
   * 选择器标题 - 只接受字符串
   */
  title?: string | React.ReactNode;
  /**
   * 日期顺序，默认为 'DMY' (日/月/年)
   */
  order?: 'DMY' | 'MDY';
  /**
   * 取消按钮文本
   */
  cancelText?: string;
  /**
   * 保存按钮文本
   */
  saveText?: string;
  /**
   * 取消按钮点击事件处理函数
   */
  onCancel?: () => void;
  /**
   * 保存按钮点击事件处理函数
   */
  onSave?: (date: Date) => void;
  /**
   * 是否可见
   */
  open?: boolean;
  /**
   * 弹窗关闭回调函数
   */
  onClose?: () => void;
  /**
   * 自定义 z-index 值
   */
  zIndex?: number;
}

const MobileDatePicker = React.forwardRef<HTMLDivElement, MobileDatePickerProps>(
  (
    {
      value,
      defaultValue = new Date(),
      onChange,
      className = '',
      dateRange = {},
      optionHeight = 44,
      visibleOptionNum = 5,
      title,
      order = 'DMY',
      cancelText,
      saveText,
      onCancel,
      onSave,
      open = false,
      onClose,
      zIndex,
    },
    ref,
  ) => {
    const t = useTranslations('mobileDatePicker');

    cancelText = cancelText || t('cancelText');

    saveText = saveText || t('saveText');
    // 使用 useMemo 包装日期范围初始化
    const minDate = React.useMemo(
      () => dateRange.minDate || new Date(new Date().getFullYear() - 80, 0, 1),
      [dateRange.minDate],
    );

    const maxDate = React.useMemo(
      () => dateRange.maxDate || new Date(new Date().getFullYear() + 20, 11, 31),
      [dateRange.maxDate],
    );

    // 使用传入的值或默认值初始化日期
    const initialDate = value || defaultValue;
    const [selectedDate, setSelectedDate] = useState<Date>(() => {
      if (initialDate < minDate) return minDate;
      if (initialDate > maxDate) return maxDate;
      return initialDate;
    });

    // 用于临时存储选择中的日期，直到保存才更新实际值
    const [tempDate, setTempDate] = useState<Date>(selectedDate);

    // 初始化年、月、日
    const [selectedDay, setSelectedDay] = useState<number>(tempDate.getDate());
    const [selectedMonth, setSelectedMonth] = useState<number>(tempDate.getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(tempDate.getFullYear());

    // 生成日、月、年的选项数据
    const generateYears = () => {
      const startYear = Math.min(minDate.getFullYear(), new Date().getFullYear());
      const endYear = Math.max(maxDate.getFullYear(), new Date().getFullYear());
      const years = [];

      for (let year = startYear; year <= endYear; year++) {
        years.push({
          label: `${year}`,
          value: year,
        });
      }

      return years;
    };

    // 使用 useCallback 包装 generateMonths 函数
    const generateMonths = React.useCallback(() => {
      const months = [];

      const currentYear = selectedYear;
      const startMonth = currentYear === minDate.getFullYear() ? minDate.getMonth() : 0;
      const endMonth = currentYear === maxDate.getFullYear() ? maxDate.getMonth() : 11;

      for (let month = startMonth; month <= endMonth; month++) {
        // 使用数字格式的月份 (01-12)，而不是英文名称
        const monthNumber = month + 1;
        const formattedMonth = monthNumber < 10 ? `0${monthNumber}` : `${monthNumber}`;

        months.push({
          label: formattedMonth,
          value: month,
        });
      }

      return months;
    }, [selectedYear, minDate, maxDate]);

    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month + 1, 0).getDate();
    };

    // 使用 useCallback 包装 generateDays 函数
    const generateDays = React.useCallback(() => {
      const days = [];
      const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);

      const startDay =
        selectedYear === minDate.getFullYear() && selectedMonth === minDate.getMonth()
          ? minDate.getDate()
          : 1;

      const endDay =
        selectedYear === maxDate.getFullYear() && selectedMonth === maxDate.getMonth()
          ? maxDate.getDate()
          : daysInMonth;

      for (let day = startDay; day <= endDay; day++) {
        // 使用补零格式的天数 (01-31)
        const formattedDay = day < 10 ? `0${day}` : `${day}`;

        days.push({
          label: formattedDay,
          value: day,
        });
      }

      return days;
    }, [selectedYear, selectedMonth, minDate, maxDate]);

    const [years] = useState(() => generateYears());
    const [months, setMonths] = useState(() => generateMonths());
    const [days, setDays] = useState(() => generateDays());

    // 根据选中的年月更新天数
    useEffect(() => {
      setDays(generateDays());
    }, [selectedYear, selectedMonth, generateDays]);

    // 根据选中的年更新月份
    useEffect(() => {
      setMonths(generateMonths());

      // 确保选中的月份在有效范围内
      if (selectedYear === minDate.getFullYear() && selectedMonth < minDate.getMonth()) {
        setSelectedMonth(minDate.getMonth());
      } else if (selectedYear === maxDate.getFullYear() && selectedMonth > maxDate.getMonth()) {
        setSelectedMonth(maxDate.getMonth());
      }
    }, [selectedYear, generateMonths, minDate, maxDate, selectedMonth]);

    // 当打开弹窗时重置临时日期
    useEffect(() => {
      if (open) {
        setTempDate(selectedDate);
        setSelectedDay(selectedDate.getDate());
        setSelectedMonth(selectedDate.getMonth());
        setSelectedYear(selectedDate.getFullYear());
      }
    }, [open, selectedDate]);

    // 根据单个选择器的变化更新临时日期
    useEffect(() => {
      // 检查日期是否有效，调整为有效的日期
      const daysInSelectedMonth = getDaysInMonth(selectedYear, selectedMonth);
      const validDay = Math.min(selectedDay, daysInSelectedMonth);

      const newDate = new Date(selectedYear, selectedMonth, validDay);

      // 处理日期边界
      if (newDate < minDate) {
        setTempDate(minDate);
        setSelectedDay(minDate.getDate());
        setSelectedMonth(minDate.getMonth());
        setSelectedYear(minDate.getFullYear());
      } else if (newDate > maxDate) {
        setTempDate(maxDate);
        setSelectedDay(maxDate.getDate());
        setSelectedMonth(maxDate.getMonth());
        setSelectedYear(maxDate.getFullYear());
      } else {
        setTempDate(newDate);
        if (validDay !== selectedDay) {
          setSelectedDay(validDay);
        }
      }
    }, [selectedDay, selectedMonth, selectedYear, minDate, maxDate]);

    // 当传入的值变化时，更新内部状态
    useEffect(() => {
      if (value) {
        let validValue = value;
        if (value < minDate) validValue = minDate;
        else if (value > maxDate) validValue = maxDate;
        setSelectedDate(validValue);
      }
    }, [value, minDate, maxDate]);

    // 处理选择器值变化的函数
    const handleDayChange = (pickerValue: string) => {
      // 从选项中找到对应的日值
      const dayOption = days.find((d) => d.label === pickerValue);
      if (dayOption) {
        setSelectedDay(dayOption.value);
      } else {
        // 如果没找到，直接解析数字
        const dayNum = parseInt(pickerValue, 10);
        if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
          setSelectedDay(dayNum);
        }
      }
    };

    const handleMonthChange = (pickerValue: string) => {
      // 从选项中找到对应的月份值（0-11）
      const monthOption = months.find((m) => m.label === pickerValue);
      if (monthOption) {
        setSelectedMonth(monthOption.value);
        // 立即更新月份同步UI
      } else {
        // 如果没找到，直接解析数字并减1（月份显示为1-12，存储为0-11）
        const monthNum = parseInt(pickerValue, 10);
        if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
          setSelectedMonth(monthNum);
        }
      }
    };

    const handleYearChange = (pickerValue: string) => {
      // 从选项中找到对应的年份值
      const yearOption = years.find((y) => y.label === pickerValue);
      if (yearOption) {
        setSelectedYear(yearOption.value);
      } else {
        // 如果没找到，直接解析数字
        const yearNum = parseInt(pickerValue, 10);
        if (!isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2100) {
          setSelectedYear(yearNum);
        }
      }
    };

    const handleCancel = () => {
      // 取消时恢复到原来的日期
      setTempDate(selectedDate);
      setSelectedDay(selectedDate.getDate());
      setSelectedMonth(selectedDate.getMonth());
      setSelectedYear(selectedDate.getFullYear());

      onCancel && onCancel();
      onClose && onClose();
    };

    const handleSave = () => {
      // 保存时使用 tempDate 更新 selectedDate
      const newDate = tempDate;
      setSelectedDate(newDate);
      onChange && onChange(newDate);
      onSave && onSave(newDate);
      onClose && onClose();
    };

    const handlePopupClose = () => {
      onClose && onClose();
    };

    // 根据国际日期格式顺序(DMY 或 MDY)排列选择器
    const renderPickers = () => {
      // 日选择器
      const dayPicker = (
        <div className="flex-1">
          <Picker
            options={days}
            defaultValue={days.find((d) => d.value === selectedDay)?.label || ''}
            onChange={(pickedValue) => handleDayChange(pickedValue)}
            optionHeight={optionHeight}
            visibleOptionNum={visibleOptionNum}
            className="border-r border-gray-200"
            renderOption={(option) => (
              <div className="flex items-center justify-center h-full">{option.label}</div>
            )}
          />
        </div>
      );

      // 月选择器
      const monthPicker = (
        <div className="flex-1">
          <Picker
            options={months}
            defaultValue={months.find((m) => m.value === selectedMonth)?.label || ''}
            onChange={(pickedValue) => handleMonthChange(pickedValue)}
            optionHeight={optionHeight}
            visibleOptionNum={visibleOptionNum}
            className="border-r border-gray-200"
            renderOption={(option) => (
              <div className="flex items-center justify-center h-full">{option.label}</div>
            )}
          />
        </div>
      );

      // 年选择器
      const yearPicker = (
        <div className="flex-1">
          <Picker
            options={years}
            defaultValue={
              years.find((y) => y.value === selectedYear)?.label || String(selectedYear)
            }
            onChange={(pickedValue) => handleYearChange(pickedValue)}
            optionHeight={optionHeight}
            visibleOptionNum={visibleOptionNum}
            renderOption={(option) => (
              <div className="flex items-center justify-center h-full">{option.label}</div>
            )}
          />
        </div>
      );

      // 根据顺序返回选择器
      if (order === 'DMY') {
        return (
          <>
            {dayPicker}
            {monthPicker}
            {yearPicker}
          </>
        );
      } else {
        return (
          <>
            {monthPicker}
            {dayPicker}
            {yearPicker}
          </>
        );
      }
    };

    // 构建自定义标题组件
    const customTitle = title && (
      <div className="h-[11.733vw]  bg-white flex items-center justify-between">
        <button onClick={handleCancel} className="text-[#969799] px-[4.267vw] text-[3.733vw]">
          {cancelText}
        </button>
        <div className="font-bb text-[4.533vw] text-primary-text leading-[4.533vw]">{title}</div>
        <button
          onClick={handleSave}
          className="px-[4.267vw] text-[3.733vw] font-br text-primary-purple"
        >
          {saveText}
        </button>
      </div>
    );

    // 通过 Popup 渲染
    return (
      <Popup
        position="bottom"
        height="auto"
        open={open}
        onClose={handlePopupClose}
        title={customTitle}
        disableAutoFocus
        keepMounted={false}
        zIndex={zIndex}
      >
        <div className="mobile-date-picker" ref={ref}>
          <div className={`flex ${className}`}>{renderPickers()}</div>
        </div>
      </Popup>
    );
  },
);

MobileDatePicker.displayName = 'MobileDatePicker';

export default MobileDatePicker;
